import { useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
} from '@mui/material';
import QRCodeSVG from '../QRCodeSVG';
import AnimatedAvatar from '../AnimatedAvatar';

interface HashatarApplicationsDemoProps {
  className?: string;
}

export default function HashatarApplicationsDemo({ className }: HashatarApplicationsDemoProps) {
  const bankingRef = useRef<HTMLDivElement>(null);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const qrRef = useRef<HTMLDivElement>(null);

  // Sample data
  const iban = 'GB82 WEST 1234 5698 7654 32';
  const invoiceAmount = '£2,500.00';
  const qrData = 'GB82WEST12345698765432';

  return (
    <Box className={className}>
      <Typography
        variant="h3"
        component="h2"
        sx={{
          textAlign: 'center',
          mb: 3,
          fontWeight: 'bold',
          color: '#333',
          fontSize: { xs: '2rem', md: '2.5rem' },
        }}
      >
        Hashatar for Your Important Data
      </Typography>

      <Typography
        variant="h6"
        component="p"
        sx={{
          textAlign: 'center',
          mb: 6,
          color: '#666',
          maxWidth: '700px',
          mx: 'auto',
          lineHeight: 1.6,
        }}
      >
        See how hashatar integrates seamlessly into real-world applications - from mobile banking 
        to paper invoices and QR codes - providing visual verification wherever data accuracy matters.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: { xs: '20px', sm: '30px', md: '40px' },
          flexWrap: 'wrap',
          px: { xs: 1, sm: 2 },
        }}
      >
        {/* Mobile Banking App */}
        <Box
          sx={{
            width: { xs: '260px', sm: '270px', md: '280px' },
            height: { xs: '520px', sm: '550px', md: '580px' },
            backgroundColor: '#ffffff',
            borderRadius: '20px',
            border: '8px solid #1a1a1a',
            padding: { xs: '16px', md: '20px' },
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            position: 'relative',
            maxWidth: 'calc(100vw - 40px)', // Ensure it never exceeds viewport
          }}
        >
          {/* Status Bar */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              fontSize: '12px',
              color: '#333',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              9:41 AM
            </Typography>
            <Typography variant="caption">
              🔋 100%
            </Typography>
          </Box>

          {/* App Header */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                color: '#1976d2',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
            >
              SecureBank
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#666' }}
            >
              Mobile Banking
            </Typography>
          </Box>

          {/* Account Balance */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography
              variant="body2"
              sx={{ color: '#666', mb: 1 }}
            >
              Current Account
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: '#333',
                fontWeight: 'bold',
                fontFamily: 'Monaco, monospace',
              }}
            >
              £12,847.32
            </Typography>
          </Box>

          {/* Account Details with Hashatar */}
          <Paper
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                mb: 1,
                fontSize: '12px',
              }}
            >
              Account Details
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Monaco, monospace',
                    fontSize: '11px',
                    color: '#333',
                    wordBreak: 'break-all',
                  }}
                >
                  {iban}
                </Typography>
              </Box>
              
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  border: '1px solid #1976d2',
                  backgroundColor: '#000',
                  flexShrink: 0,
                }}
                ref={bankingRef}
              >
                <AnimatedAvatar 
                  text={iban} 
                  size={16}
                  containerRef={bankingRef}
                />
              </Box>
            </Box>
          </Paper>

          {/* Recent Transactions */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: '#666', mb: 2, fontSize: '12px' }}
            >
              Recent Transactions
            </Typography>
            {[
              { desc: 'Online Purchase', amount: '-£24.99', time: '2 hours ago' },
              { desc: 'Salary Deposit', amount: '+£3,200.00', time: '1 day ago' },
              { desc: 'Coffee Shop', amount: '-£4.50', time: '2 days ago' },
            ].map((tx, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  borderBottom: index < 2 ? '1px solid #e9ecef' : 'none',
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '11px', color: '#333' }}
                  >
                    {tx.desc}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '9px', color: '#666' }}
                  >
                    {tx.time}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '11px',
                    color: tx.amount.startsWith('+') ? '#28a745' : '#dc3545',
                    fontFamily: 'Monaco, monospace',
                  }}
                >
                  {tx.amount}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Paper Invoice */}
        <Box
          sx={{
            width: { xs: '260px', sm: '280px', md: '300px' },
            height: { xs: '360px', sm: '380px', md: '400px' },
            backgroundColor: '#ffffff',
            border: '1px solid #ddd',
            padding: { xs: '20px', sm: '25px', md: '30px' },
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            maxWidth: 'calc(100vw - 40px)', // Ensure it never exceeds viewport
          }}
        >
          {/* Company Header */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#1976d2',
                fontWeight: 'bold',
                fontSize: '16px',
                mb: 0.5,
              }}
            >
              TechCorp Solutions
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#666',
                fontSize: '9px',
                lineHeight: 1.2,
              }}
            >
              123 Business Park • London, EC1A 1BB
            </Typography>
          </Box>

          {/* Invoice Title */}
          <Typography
            variant="h5"
            sx={{
              color: '#333',
              fontWeight: 'bold',
              fontSize: '20px',
              mb: 1,
            }}
          >
            INVOICE
          </Typography>
          
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '9px',
              mb: 2,
              display: 'block',
            }}
          >
            Invoice No: INV-2024-001234
          </Typography>

          {/* Amount Due */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: '#f8f9fa',
              border: '2px solid #1976d2',
              borderRadius: '8px',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                fontSize: '10px',
                mb: 0.5,
              }}
            >
              TOTAL AMOUNT DUE
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: '#1976d2',
                fontWeight: 'bold',
                fontSize: '24px',
                fontFamily: 'Monaco, monospace',
              }}
            >
              {invoiceAmount}
            </Typography>
          </Box>

          {/* Payment Details with Prominent Hashatar */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#333',
                fontWeight: 'bold',
                mb: 1,
                fontSize: '12px',
              }}
            >
              Payment Instructions
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontSize: '9px',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  Bank Transfer:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Monaco, monospace',
                    fontSize: '11px',
                    color: '#333',
                    backgroundColor: '#fff',
                    padding: '4px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                >
                  {iban}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontSize: '8px',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  Hashatar
                </Typography>
                <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  border: '1px solid #1976d2',
                  backgroundColor: '#000',
                  flexShrink: 0,
                }}
                ref={invoiceRef}
              >
                <AnimatedAvatar 
                  text={iban} 
                  size={16}
                  containerRef={invoiceRef}
                />
              </Box>
              </Box>
            </Box>
          </Box>

          {/* Bill To */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: '#333',
                fontWeight: 'bold',
                mb: 0.5,
                fontSize: '10px',
              }}
            >
              Bill To:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#333',
                fontSize: '9px',
                lineHeight: 1.2,
              }}
            >
              Client Company Ltd.<br />
              456 Customer Street<br />
              Manchester, M1 1AA
            </Typography>
          </Box>
        </Box>

        {/* QR Code with Embedded Hashatar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: '#333',
              textAlign: 'center',
            }}
          >
            QR Code Verification
          </Typography>

          {/* QR Code */}
          <Paper
            sx={{
              p: 3,
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e0e0e0',
            }}
          >
            <Box
              sx={{
                width: 200,
                height: 200,
                backgroundColor: '#fff',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #e0e0e0',
                position: 'relative',
              }}
            >
              <QRCodeSVG
                value={qrData}
                size={160}
                level="M"
                includeMargin={false}
              />
              
              {/* Embedded Hashatar in center */}
              <Box
                  sx={{
                    position: 'absolute',
                    top: 'calc(50% + 3px)',
                    left: 'calc(50% + 3px)',
                    transform: 'translate(-50%, -50%)',
                    width: 38,
                    height: 39,
                  }}
                >
                  <Box 
                    sx={{ 
                      height: '100%', 
                      width: '100%',
                    }} 
                    ref={qrRef} 
                    id="demo-avatar"
                  >
                    <AnimatedAvatar text={iban} size={16} containerRef={qrRef} />
                  </Box>
                </Box>
            </Box>
          </Paper>

          <Box sx={{ textAlign: 'center', maxWidth: { xs: '160px', sm: '180px', md: '200px' } }}>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                fontSize: '12px',
                lineHeight: 1.4,
                mb: 1,
              }}
            >
              QR code with embedded hashatar for dual verification
            </Typography>
            
            <Box
              sx={{
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                padding: '8px',
                border: '1px solid #e9ecef',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'Monaco, monospace',
                  fontSize: '9px',
                  color: '#333',
                  wordBreak: 'break-all',
                }}
              >
                {iban}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
